import {
  IContentImageTemplate,
  IContentImageTemplateDataUpload,
  IContentImageTemplateDataUrl
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { CS } from '@dadohr/shared/versions';
import {
  FormHelperText,
  IconButton,
  LinearProgress,
  Slider,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  styled
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { capitalize } from 'lodash';
import { Image, Trash2 } from 'lucide-react';
import React, { SetStateAction, useRef, useState } from 'react';
import shortid from 'shortid';
import { CustomInputLabel } from '../../../../../../../../components/CustomInputLabel';
import { DataFieldWithLabel } from '../../../../../../../../components/DataFieldWithLabel';
import { Dropzone } from '../../../../../../../../components/Dropzone';
import { FormRow } from '../../../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { ONE_MB } from '../../../../../../../../services/bytes';
import {
  ICloudStorageFile,
  storeFile
} from '../../../../../../../../services/storage';
import { TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  VALIDATORS_IMAGE,
  resetBlockValidation,
  useFieldValidator
} from '../../../validation';
import { ReactComponent as ImageAlignCenter } from './ImageAlignCenter.svg';
import { ReactComponent as ImageAlignLeft } from './ImageAlignLeft.svg';
import { ReactComponent as ImageAlignRight } from './ImageAlignRight.svg';

const TabContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(4),
  marginBottom: p.theme.spacing(4),
  background: p.theme.palette.background.paper
}));

const TabPanel: React.FC<{ active: boolean }> = ({ active, children }) => {
  return (
    <div role="tabpanel" hidden={!active}>
      {active && <TabContainer>{children}</TabContainer>}
    </div>
  );
};

const ImageAligment = ({
  value,
  onChange
}: {
  value: IContentImageTemplate;
  onChange: (nextValue: IContentImageTemplate) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <CustomInputLabel label={capitalize(t('align.verb'))} fullWidth>
      <ToggleButtonGroup
        exclusive
        aria-label="image alignment"
        value={value.data.align}
        onChange={(ev, nextAlign: 'left' | 'right' | 'center' | null) => {
          if (nextAlign === null) {
            return;
          }
          onChange({
            ...value,
            data: {
              ...value.data,
              align: nextAlign
            }
          });
        }}
      >
        <ToggleButton value="left" aria-label="left aligned">
          <Tooltip title={capitalize(t('align.left'))}>
            <ImageAlignLeft />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="center" aria-label="center aligned">
          <Tooltip title={capitalize(t('align.center'))}>
            <ImageAlignCenter />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="right" aria-label="right aligned">
          <Tooltip title={capitalize(t('align.right'))}>
            <ImageAlignRight />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </CustomInputLabel>
  );
};

const ImageSize = ({
  value,
  onChange
}: {
  value: IContentImageTemplate;
  onChange: (nextValue: IContentImageTemplate) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <CustomInputLabel label={capitalize(t('Size'))} fullWidth>
      <Image size={20} strokeWidth={2} />
      <Slider
        marks
        aria-label={t('Image Size')}
        min={25}
        max={100}
        step={25}
        defaultValue={25}
        value={value.data.width}
        onChange={(ev, nextWidth: number | number[]) => {
          onChange({
            ...value,
            data: {
              ...value.data,
              width: nextWidth as number
            }
          });
        }}
      />
      <Image size={24} strokeWidth={2} />
    </CustomInputLabel>
  );
};

const ImageDisplaySettings = ({
  value,
  onChange
}: {
  value: IContentImageTemplate;
  onChange: (nextValue: IContentImageTemplate) => void;
}) => {
  return (
    <>
      <FormRow>
        <ImageSize value={value} onChange={onChange} />
      </FormRow>
      <FormRow>
        <ImageAligment value={value} onChange={onChange} />
      </FormRow>
    </>
  );
};

const ImageTitle = ({
  value,
  onChange
}: {
  value: IContentImageTemplate;
  onChange: (nextValue: IContentImageTemplate) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <TextField
      label={t('Image Title')}
      value={value.data.alt}
      fullWidth
      variant="outlined"
      onChange={(ev) => {
        onChange({
          ...value,
          data: {
            ...value.data,
            alt: ev.target.value
          }
        });
      }}
    />
  );
};

const MAX_FILE_SIZE_MB = 5;

const SUPPORTED_FILE_TYPES = [
  {
    contentType: 'image/jpeg',
    label: 'jpg'
  },
  {
    contentType: 'image/png',
    label: 'png'
  },
  {
    contentType: 'image/gif',
    label: 'gif'
  }
];

const uploadFile = async (orgId: string, f: File) => {
  const storagePath = `${CS.assets}/${orgId}/${shortid()}`;
  const file: ICloudStorageFile = {
    name: storagePath,
    data: f,
    contentType: f.type,
    metadata: {
      orgId,
      originalFileName: f.name
    }
  };

  const docRef = await storeFile(file);
  const src = await docRef.getDownloadURL();

  return {
    origFileName: f.name,
    storagePath,
    src
  };
};

const removeFile = async (d: IContentImageTemplateDataUpload) => {
  // TODO
};

const FileItemContainer = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'min-content 1fr min-content',
  gridColumnGap: p.theme.spacing(1),
  alignItems: 'center'
}));

const FileItemText = styled('div')((p) => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap'
}));

const BlockSettingsContentImageUpload = ({
  value,
  onChange,
  expTpl
}: {
  value: IContentImageTemplate;
  onChange: (nextValue: IContentImageTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const [uploadError, setUploadError] = useState('');
  const [processing, setProcessing] = useState(false);
  return (
    <>
      <FormRow>
        <ImageTitle value={value} onChange={onChange} />
      </FormRow>
      <FormRow>
        <div style={{ width: '100%' }}>
          {value.data.type === 'upload' && value.data.origFileName ? (
            <FileItemContainer>
              <Image size={16} />
              <FileItemText>{value.data.origFileName}</FileItemText>
              <IconButton
                onClick={async () => {
                  try {
                    // try to be nice - if it doesn't work, don't hold the user up.
                    if (value.data.type === 'upload') {
                      await removeFile(value.data);
                    }
                  } catch {}

                  onChange({
                    ...value,
                    data: {
                      type: 'upload',
                      alt: value.data.alt,
                      align: value.data.align,
                      width: value.data.width,
                      src: '',
                      origFileName: '',
                      storagePath: ''
                    }
                  });
                }}
              >
                <Trash2 size={16} />
              </IconButton>
            </FileItemContainer>
          ) : processing ? (
            <>
              <LinearProgress variant="indeterminate" color="primary" />
              <FormHelperText>{t('Uploading your image...')}</FormHelperText>
            </>
          ) : (
            <>
              <Dropzone
                label={t('Drop image or click to select')}
                onDrop={async (fs) => {
                  if (!fs.length) {
                    setUploadError(t('Please choose a file'));
                    return;
                  }

                  if (fs.length > 1) {
                    setUploadError(t('Please select only a single image'));
                    return;
                  }
                  const f = fs[0];
                  if (
                    !SUPPORTED_FILE_TYPES.find((t) => t.contentType === f.type)
                  ) {
                    setUploadError(t('Please choose a valid file type'));
                    return;
                  }

                  if (f.size > MAX_FILE_SIZE_MB * ONE_MB) {
                    setUploadError(
                      t('Please choose a file smaller than {{ maxFileSize }}', {
                        maxFileSize: `${MAX_FILE_SIZE_MB}MB`
                      })
                    );
                    return;
                  }

                  setProcessing(true);
                  setUploadError('');
                  try {
                    const data = await uploadFile(expTpl.orgId, f);
                    onChange({
                      ...value,
                      data: {
                        type: 'upload',
                        alt: value.data.alt,
                        align: value.data.align,
                        width: value.data.width,
                        ...data
                      }
                    });
                  } catch (err: any) {
                    console.log('UPLOAD_ERROR', err);
                    setUploadError(t('Failed to upload file'));
                  }
                  setProcessing(false);
                }}
              />

              <FormHelperText error={!!uploadError}>
                {t('Max {{ maxFileSize }}', {
                  maxFileSize: `${MAX_FILE_SIZE_MB}MB`
                })}
                {' • '}
                {t('Supported formats')}:{' '}
                {SUPPORTED_FILE_TYPES.map((t) => t.label).join(', ')}
              </FormHelperText>
              {uploadError && (
                <FormHelperText error={true}>{uploadError}</FormHelperText>
              )}
            </>
          )}
        </div>
      </FormRow>
      <ImageDisplaySettings value={value} onChange={onChange} />
    </>
  );
};

const BlockSettingsContentImageURL = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl
}: {
  value: IContentImageTemplate;
  onChange: (nextValue: IContentImageTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const validateUrl = useFieldValidator(setValidation, VALIDATORS_IMAGE, 'url');
  const invalidUrlReason = validation?.fields.url || null;
  return (
    <>
      <FormRow>
        <ImageTitle value={value} onChange={onChange} />
      </FormRow>
      <FormRow>
        <TextField
          label={t('Image URL')}
          value={value.data.src}
          fullWidth
          variant="outlined"
          onBlur={() => validateUrl(value, expTpl)}
          error={!!invalidUrlReason}
          helperText={invalidUrlReason}
          onChange={(ev) => {
            const nextValue = {
              ...value,
              data: {
                ...value.data,
                src: ev.target.value
              }
            };

            invalidUrlReason && validateUrl(nextValue, expTpl);
            onChange(nextValue);
          }}
        />
      </FormRow>

      <ImageDisplaySettings value={value} onChange={onChange} />
    </>
  );
};

export const BlockSettingsContentImage = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl
}: {
  value: IContentImageTemplate;
  onChange: (nextValue: IContentImageTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
}) => {
  // We use this so that information about an uploaded image is not
  // immediately lost when a user just switches tabs.
  const prevData = useRef<{
    url?: IContentImageTemplateDataUrl;
    upload?: IContentImageTemplateDataUpload;
  }>({ [value.data.type]: value });

  const change = (nextValue: IContentImageTemplate) => {
    if (nextValue.data.type === 'url') {
      prevData.current[nextValue.data.type] = nextValue.data;
    }
    if (nextValue.data.type === 'upload') {
      prevData.current[nextValue.data.type] = nextValue.data;
    }
    onChange(nextValue);
  };

  return (
    <>
      <Tabs
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
        value={value.data.type}
        onChange={(event, nextImageMode: 'url' | 'upload') => {
          resetBlockValidation(value.id, setValidation);
          if (nextImageMode === 'url') {
            change({
              ...value,
              data: {
                type: 'url',
                src: prevData.current.url?.src || '',
                alt: value.data.alt,
                align: value.data.align,
                width: value.data.width
              }
            });
          }
          if (nextImageMode === 'upload') {
            change({
              ...value,
              data: {
                type: 'upload',
                src: prevData.current.upload?.src || '',
                origFileName: prevData.current.upload?.origFileName || '',
                storagePath: prevData.current.upload?.storagePath || '',
                alt: value.data.alt,
                align: value.data.align,
                width: value.data.width
              }
            });
          }
        }}
      >
        <Tab value="url" label="Enter URL" />
        <Tab value="upload" label="Upload Image" />
      </Tabs>

      <TabPanel active={value.data.type === 'upload'}>
        <BlockSettingsContentImageUpload
          value={value}
          onChange={change}
          validation={validation}
          setValidation={setValidation}
          expTpl={expTpl}
        />
      </TabPanel>
      <TabPanel active={value.data.type === 'url'}>
        <BlockSettingsContentImageURL
          value={value}
          onChange={change}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
        />
      </TabPanel>
    </>
  );
};

export const BlockSettingsContentImageReadOnly = ({
  value
}: {
  value: IContentImageTemplate;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <FormRow>
        <DataFieldWithLabel label={t('Image Title')}>
          {value.data.alt || '-'}
        </DataFieldWithLabel>
      </FormRow>
      <FormRow>
        {value.data.type === 'url' && (
          <DataFieldWithLabel label={t('Image URL')}>
            {value.data.src || '-'}
          </DataFieldWithLabel>
        )}
        {value.data.type === 'upload' && (
          <DataFieldWithLabel label={t('Filename')}>
            {value.data.origFileName || '-'}
          </DataFieldWithLabel>
        )}
      </FormRow>
      <FormRow>
        <DataFieldWithLabel label={capitalize(t('align.verb'))}>
          {value.data.align === 'center'
            ? capitalize(t('align.center'))
            : value.data.align === 'left'
            ? capitalize(t('align.left'))
            : value.data.align === 'right'
            ? capitalize(t('align.right'))
            : '-'}
        </DataFieldWithLabel>
      </FormRow>
    </>
  );
};
